import { useState, useCallback, Dispatch, SetStateAction } from 'react'
import _ from 'lodash'
import { CheckoutAdditionalField } from '../types'
import { INPUT_TYPE } from '../constants'

type UseCheckoutAdditionalFieldsFormOptions = {
  onSubmit: (fields: CheckoutAdditionalField[]) => Promise<void>
  onValidationError?: (message: string) => void
  defaultFormData?: CheckoutAdditionalField[]
}

type UseCheckoutAdditionalFieldsFormReturn = {
  formData: CheckoutAdditionalField[]
  setFormData: Dispatch<SetStateAction<CheckoutAdditionalField[]>>
  addField: () => void
  updateField: (index: number, updatedField: CheckoutAdditionalField) => void
  handleSubmit: () => Promise<void>
  isDirty: boolean
}

/**
 * チェックアウト追加フィールドのフォーム管理フック
 * - フィールドの追加・更新
 * - フォームの送信処理
 */
export const useCheckoutAdditionalFieldsForm = (
  options: UseCheckoutAdditionalFieldsFormOptions
): UseCheckoutAdditionalFieldsFormReturn => {
  const [formData, setFormData] = useState<CheckoutAdditionalField[]>(options.defaultFormData || [])

  const addField = useCallback(() => {
    setFormData((prevFields) => [...prevFields, {
      id: undefined,
      displayName: '',
      inputType: INPUT_TYPE.TEXT,
      requiredFlag: false,
      displayFlag: false,
      options: [],
    }])
  }, [])

  const updateField = useCallback(
    (index: number, updatedField: CheckoutAdditionalField) => {
      setFormData((prevFields) =>
        prevFields.map((field, i) => (i === index ? updatedField : field))
      )
    },
    []
  )

  const handleSubmit = useCallback(async () => {
    const displayedFields = formData.filter((field) => field.displayFlag)
    const fieldsWithoutName = displayedFields.filter((field) => !field.displayName?.trim())

    if (fieldsWithoutName.length > 0) return options.onValidationError?.('表示される追加項目には、すべて表示名を設定してください。')

    await options.onSubmit(formData)
  }, [formData, options])

  const isDirty = !_.isEqual(options.defaultFormData, formData)

  return {
    formData,
    setFormData,
    addField,
    updateField,
    handleSubmit,
    isDirty,
  }
}
